import styles from './styles.module.css';
import { Navigate, useParams } from '@/util/react-router-dom';
import {
  RegisterSuccessResponse,
  RegisterExternalPatientErrorResponse,
  RegisterExternalPatientErrorType,
  RegisterExternalPatientRequest,
  RegisterExternalPatientWithEmailRequestType,
  RegisterExternalPatientCredentialsOnlyRequestType,
} from '@/api/api.schemas';
import { useTranslation } from '@/contexts/i18n-context';
import { Section } from '@/components/containers/section';
import { useBranding } from '@/outlets/branding-outlet';
import { useRegisterExternalPatient } from '@/api/register-external-patient';
import { RegisterLayout } from '@/components/auth/register-layout';
import {
  RegisterCredentialsOnlyFormData,
  RegisterCredentialsWithEmailFormData,
  RegisterForm,
} from '@/components/auth/register-form';
import { Loader } from '@/components/loaders/loader';
import { useRegisterExternalPatientSeeds } from '@/api/get-register-external-patient-seed';

export function RegisterExternalPatientPage() {
  const { t } = useTranslation();

  function getErrorMessage(error?: RegisterExternalPatientErrorResponse | null) {
    if (!error) {
      return;
    }

    switch (error.type) {
      case RegisterExternalPatientErrorType.UserAlreadyExists:
        return t('register.errors.userAlreadyExists');
      case RegisterExternalPatientErrorType.ValidationFailed:
        return t('register.errors.validationFailed');
      case RegisterExternalPatientErrorType.PayloadMissing:
        return t('register.errors.externalPatientPayloadMissing');
      case RegisterExternalPatientErrorType.PayloadAlreadyUsed:
        return t('register.errors.externalPatientPayloadAlreadyUsed');
      default:
        return t('common.errors.unknown');
    }
  }

  const { mutateAsync: registerExternalPatient, isPending, error } = useRegisterExternalPatient();
  const { createExternalPatientEnabled, networkId } = useBranding();
  const { seedKey } = useParams<{ seedKey: string }>();
  const {
    data,
    isFetching,
    error: hasEmailError,
  } = useRegisterExternalPatientSeeds(seedKey!, {
    query: { enabled: !!seedKey },
  });

  if (isFetching) {
    return <Loader type="large" />;
  }

  if (!createExternalPatientEnabled) {
    return <Navigate to="/default" replace doNotResolveBrandingPath />;
  }

  if (!seedKey) {
    return <Navigate to="/login" replace />;
  }

  if (hasEmailError) {
    switch (hasEmailError.type) {
      case RegisterExternalPatientErrorType.PayloadAlreadyUsed:
        return <Navigate to="/login" replace />;
      case RegisterExternalPatientErrorType.PayloadMissing:
        return <Navigate to="/system-error" replace />;
    }
  }

  const onSubmit = (
    formData: RegisterCredentialsOnlyFormData | RegisterCredentialsWithEmailFormData,
    onSuccess: (result: RegisterSuccessResponse) => void,
  ) => {
    const common = { networkId, seedKey };
    const payload: RegisterExternalPatientRequest =
      'email' in formData
        ? {
            type: RegisterExternalPatientWithEmailRequestType.credentialsWithEmail,
            ...common,
            ...formData,
          }
        : {
            type: RegisterExternalPatientCredentialsOnlyRequestType.credentialsOnly,
            ...common,
            ...formData,
          };

    registerExternalPatient({ data: payload }, { onSuccess });
  };

  return (
    <RegisterLayout>
      <Section isFormSection={false}>
        <h2 className="h4">{t('register.formTitle')}</h2>
        <p className={styles.helperText}>{t('register.externalPatientHelperText')}</p>
        <RegisterForm
          type={data?.hasEmail ? 'credentials-only' : 'credentials-with-email'}
          onSubmit={onSubmit}
          error={getErrorMessage(error)}
          loading={isPending}
        />
      </Section>
    </RegisterLayout>
  );
}
